import { Box, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import LockResetRounded from "@mui/icons-material/LockResetRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import TextField from "./TextField";
import PasswordTextField from "./PasswordField";
import Button from "./Button";
import Account from "../api/Account";
import ErrorDialog from "./ErrorDialog";
import { FONTS, GLASS_SX, CARD_STYLES } from "../constants/styles";

const INDIGO = "#3B2F80";
const INDIGO_LIGHT = "#E8E5F4";
const GRADIENT_TOP = INDIGO;
const GRADIENT_BOTTOM = "#1A1145";
const NOISE_TEXTURE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

const FIELD_SX = {
    bgcolor: INDIGO_LIGHT,
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent !important",
    },
    "& .MuiInputAdornment-root div": {
        backgroundColor: INDIGO + " !important",
    },
} as const;

const AuthShell = ({ children }: { children: React.ReactNode }) => (
    <Stack
        width="100%"
        minHeight="100dvh"
        alignItems="center"
        justifyContent="center"
        sx={{
            background: `linear-gradient(180deg, ${GRADIENT_TOP} 0%, ${GRADIENT_BOTTOM} 100%)`,
            position: "relative",
            overflowY: "auto",
            px: 2,
            py: 4,
            boxSizing: "border-box",
            "&::after": {
                content: '""',
                position: "fixed",
                inset: 0,
                opacity: 0.04,
                backgroundImage: NOISE_TEXTURE,
                backgroundRepeat: "repeat",
                backgroundSize: "128px 128px",
                pointerEvents: "none",
            },
        }}
    >
        {children}
    </Stack>
);

const ForgotPassword = () => {
    const { token } = useParams(); // gets token from /forgot-password/:token

    return (
        <AuthShell>
            {!token ? <RequestResetForm /> : <ResetPasswordForm token={token} />}
        </AuthShell>
    );
};

const RequestResetForm = () => {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorOpen, setErrorOpen] = useState(false);

    const onSubmit = async () => {
        if (!email) {
            setErrorOpen(true);
            return;
        }
        setLoading(true);
        try {
            await Account.requestPasswordReset(email);
            setSuccess(true);
        } catch (err) {
            console.error("Request error:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* Icon */}
            <Box
                sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "18px",
                    ...GLASS_SX,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
                    mb: 2,
                    color: "white",
                    "@keyframes logoReveal": {
                        from: { opacity: 0, transform: "scale(0.85)" },
                        to: { opacity: 1, transform: "scale(1)" },
                    },
                    animation: "logoReveal 0.5s ease-out",
                }}
            >
                {success ? (
                    <CheckCircleRounded sx={{ fontSize: 32 }} />
                ) : (
                    <LockResetRounded sx={{ fontSize: 32 }} />
                )}
            </Box>

            {/* Heading */}
            <Typography
                fontFamily={FONTS.display}
                fontSize="26px"
                fontWeight={700}
                color="white"
                lineHeight={1.2}
                sx={{
                    "@keyframes fadeDown": {
                        from: { opacity: 0, transform: "translateY(-8px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                    animation: "fadeDown 0.5s ease-out 0.08s both",
                }}
            >
                {success ? "Check your inbox" : "Forgot your password?"}
            </Typography>
            <Typography
                fontSize="14px"
                color="rgba(255, 255, 255, 0.65)"
                textAlign="center"
                sx={{
                    animation: "fadeDown 0.5s ease-out 0.14s both",
                    mb: 3,
                    maxWidth: 320,
                }}
            >
                {success
                    ? "We've sent reset instructions if the email is registered."
                    : "Enter your email and we'll send you a reset link."}
            </Typography>

            {!success && (
                <Stack
                    sx={{
                        ...CARD_STYLES,
                        width: "100%",
                        maxWidth: 400,
                        px: 2.5,
                        py: 3,
                        boxSizing: "border-box",
                        "@keyframes cardSlideUp": {
                            from: { opacity: 0, transform: "translateY(20px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                        animation: "cardSlideUp 0.5s ease-out 0.2s both",
                    }}
                    spacing={2}
                >
                    <TextField
                        variant="outlined"
                        fullWidth
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e: any) =>
                            setEmail(e.currentTarget.value.toLowerCase())
                        }
                        onEnter={onSubmit}
                        sx={FIELD_SX}
                    />
                    <Button
                        variant="big"
                        fullWidth
                        disabled={!email || loading}
                        onClick={onSubmit}
                        customColor={INDIGO}
                        textColor="white"
                    >
                        Send reset link
                    </Button>
                </Stack>
            )}

            {/* Back to login */}
            <Typography
                fontSize="14px"
                color="rgba(255, 255, 255, 0.6)"
                sx={{
                    mt: 3,
                    cursor: "pointer",
                    animation: "fadeDown 0.5s ease-out 0.35s both",
                    transition: "color 0.2s ease",
                    "&:hover": { color: "rgba(255, 255, 255, 0.9)" },
                }}
                onClick={() => (window.location.href = "/login")}
            >
                <Box
                    component="span"
                    sx={{ color: "white", fontWeight: 600 }}
                >
                    Back to login
                </Box>
            </Typography>

            <ErrorDialog
                open={errorOpen}
                errorMessage={"Please enter your email to continue."}
                onClose={() => setErrorOpen(false)}
            />
        </>
    );
};

const ResetPasswordForm = ({ token }: { token: string }) => {
    const navigate = useNavigate();
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [mismatch, setMismatch] = useState(false);
    const [errorOpen, setErrorOpen] = useState(false);

    const onSubmit = async () => {
        if (!newPassword || newPassword !== confirmPassword) return;
        setLoading(true);
        try {
            await Account.resetPassword(token, newPassword);
            setSuccess(true);
        } catch (err) {
            console.error("Reset error:", err);
            setErrorOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmBlur = () => {
        setMismatch(confirmPassword !== "" && confirmPassword !== newPassword);
    };

    return (
        <>
            {/* Icon */}
            <Box
                sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "18px",
                    ...GLASS_SX,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
                    mb: 2,
                    color: "white",
                    "@keyframes logoReveal": {
                        from: { opacity: 0, transform: "scale(0.85)" },
                        to: { opacity: 1, transform: "scale(1)" },
                    },
                    animation: "logoReveal 0.5s ease-out",
                }}
            >
                {success ? (
                    <CheckCircleRounded sx={{ fontSize: 32 }} />
                ) : (
                    <LockResetRounded sx={{ fontSize: 32 }} />
                )}
            </Box>

            {/* Heading */}
            <Typography
                fontFamily={FONTS.display}
                fontSize="26px"
                fontWeight={700}
                color="white"
                lineHeight={1.2}
                sx={{
                    "@keyframes fadeDown": {
                        from: { opacity: 0, transform: "translateY(-8px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                    animation: "fadeDown 0.5s ease-out 0.08s both",
                }}
            >
                {success ? "Password updated" : "Set a new password"}
            </Typography>
            <Typography
                fontSize="14px"
                color="rgba(255, 255, 255, 0.65)"
                textAlign="center"
                sx={{
                    animation: "fadeDown 0.5s ease-out 0.14s both",
                    mb: 3,
                    maxWidth: 320,
                }}
            >
                {success
                    ? "You can now log in with your new password."
                    : "Choose a strong password for your account."}
            </Typography>

            <Stack
                sx={{
                    ...CARD_STYLES,
                    width: "100%",
                    maxWidth: 400,
                    px: 2.5,
                    py: 3,
                    boxSizing: "border-box",
                    "@keyframes cardSlideUp": {
                        from: { opacity: 0, transform: "translateY(20px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                    animation: "cardSlideUp 0.5s ease-out 0.2s both",
                }}
                spacing={2}
            >
                {success ? (
                    <Button
                        variant="big"
                        fullWidth
                        onClick={() => navigate("/login")}
                        customColor={INDIGO}
                        textColor="white"
                    >
                        Go to Login
                    </Button>
                ) : (
                    <>
                        <PasswordTextField
                            variant="outlined"
                            fullWidth
                            placeholder="New password"
                            value={newPassword}
                            onChange={(e: any) =>
                                setNewPassword(e.currentTarget.value)
                            }
                            sx={FIELD_SX}
                        />
                        <PasswordTextField
                            variant="outlined"
                            fullWidth
                            placeholder="Confirm password"
                            value={confirmPassword}
                            onChange={(e: any) =>
                                setConfirmPassword(e.currentTarget.value)
                            }
                            onBlur={handleConfirmBlur}
                            onEnter={onSubmit}
                            sx={FIELD_SX}
                        />
                        {mismatch && (
                            <Typography fontSize="14px" color="error">
                                Passwords do not match
                            </Typography>
                        )}
                        <Button
                            variant="big"
                            fullWidth
                            disabled={
                                !newPassword ||
                                newPassword !== confirmPassword ||
                                loading
                            }
                            onClick={onSubmit}
                            customColor={INDIGO}
                            textColor="white"
                        >
                            Change password
                        </Button>
                    </>
                )}
            </Stack>

            {/* Back to login */}
            {!success && (
                <Typography
                    fontSize="14px"
                    color="rgba(255, 255, 255, 0.6)"
                    sx={{
                        mt: 3,
                        cursor: "pointer",
                        animation: "fadeDown 0.5s ease-out 0.35s both",
                        transition: "color 0.2s ease",
                        "&:hover": { color: "rgba(255, 255, 255, 0.9)" },
                    }}
                    onClick={() => (window.location.href = "/login")}
                >
                    <Box
                        component="span"
                        sx={{ color: "white", fontWeight: 600 }}
                    >
                        Back to login
                    </Box>
                </Typography>
            )}

            <ErrorDialog
                open={errorOpen}
                errorMessage={
                    "This reset link is no longer valid. It may have already been used or expired."
                }
                onClose={() => navigate("/forgot-password")}
            />
        </>
    );
};

export default ForgotPassword;
